import { ProTable } from '@ant-design/pro-components';
import classNames from 'classnames';
import { useMemo } from 'react';

import type { ProColumns, ProTableProps } from '@ant-design/pro-components';

const FALLBACK_COL_WIDTH = 160;
/** 预留给边框、勾选列等，避免 scroll.x 刚好贴齐时仍滑不动 */
const SCROLL_X_GUTTER = 48;

const columnKey = (column: ProColumns<any>, index: number, parentKey = ''): string => {
    if (column.key != null && column.key !== '') {
        return String(column.key);
    }
    if (Array.isArray(column.dataIndex)) {
        return column.dataIndex.map(String).join('.');
    }
    if (column.dataIndex != null && column.dataIndex !== '') {
        return String(column.dataIndex);
    }
    return parentKey ? `${parentKey}-${index}` : `col-${index}`;
};

const columnWidth = (column: ProColumns<any>): number => {
    if (typeof column.width === 'number' && column.width > 0) {
        return column.width;
    }
    if (typeof column.width === 'string') {
        const parsed = Number.parseInt(column.width, 10);
        if (!Number.isNaN(parsed) && parsed > 0) {
            return parsed;
        }
    }
    return FALLBACK_COL_WIDTH;
};

const normalizeColumns = <T extends Record<string, any>>(
    columns: ProColumns<T>[] | undefined,
    parentKey = '',
): ProColumns<T>[] | undefined => {
    if (!columns) return columns;
    return columns.map((column, index) => {
        const key = columnKey(column, index, parentKey);
        return {
            ...column,
            key,
            width: column.width ?? columnWidth(column),
            children: column.children
                ? normalizeColumns(column.children as ProColumns<T>[], key)
                : column.children,
        };
    });
};

const sumColumnWidths = (columns: ProColumns<any>[] | undefined): number => {
    if (!columns?.length) return 0;
    return columns.reduce((sum, column) => {
        if (column.children?.length) {
            return sum + sumColumnWidths(column.children as ProColumns<any>[]);
        }
        return sum + columnWidth(column);
    }, 0);
};

/**
 * 统一处理列表横滑：列设置「固定在列首/列尾」依赖列宽和 scroll.x。
 * 缺 key/width 或 scroll.x 过小时，左右固定列会叠满可视区，中间无法拖动。
 */
function AdminProTable<T extends Record<string, any>, U extends Record<string, any> = any>(
    props: ProTableProps<T, U>,
) {
    const { columns, scroll, className, ...rest } = props;
    const normalizedColumns = useMemo(() => normalizeColumns(columns), [columns]);
    const mergedScroll = useMemo(() => {
        const contentWidth = sumColumnWidths(normalizedColumns);
        const explicitX = typeof scroll?.x === 'number' ? scroll.x : 0;
        return {
            ...scroll,
            x: Math.max(explicitX, contentWidth + SCROLL_X_GUTTER),
        };
    }, [normalizedColumns, scroll]);

    return (
        <ProTable<T, U>
            {...rest}
            className={classNames('admin-pro-table', className)}
            columns={normalizedColumns}
            scroll={mergedScroll}
        />
    );
}

export default AdminProTable;
